import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Grow from '@mui/material/Grow'

import UndoIcon from '@mui/icons-material/UndoRounded'
import PlayArrow from '@mui/icons-material/PlayArrowRounded'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import ThumbDownIcon from '@mui/icons-material/ThumbDownRounded'
import ThumbUpIcon from '@mui/icons-material/ThumbUpRounded'
import {
  GlassButton,
  GlassCard,
  GlassOutlineButton,
} from '@components/common/Glass'
import { ShareButton } from '@components/common/ShareButton'
import { Item } from '@components/common/ItemGuessBox'
import { List } from 'src/api/types/lists'
import { DailyListResult } from 'src/pages/Dashboard'
import { UserState } from 'src/app/AppContext'
import { UserSoloStats } from 'src/api/types/users'
import { AlertInput } from '@components/common/Alert'
import {
  altColorLighter,
  altColorDarkText,
  borderColor,
  headingFont,
  helperTextColor,
  mainColorDarkText,
  mainColorLighter,
  navy,
  shadow2,
} from 'src/ui/theme/tokens'
import { GameSounds } from 'src/ui/sound/types'
import { ratePoll } from 'src/api/polls'
import { err } from 'src/utils/functions'
import LinearProgress from '@mui/material/LinearProgress'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import { OnlineRematchState, Player } from 'src/pages/Dashboard'
import Tooltip from '@mui/material/Tooltip'
import Chip from '@mui/material/Chip'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import { reportList } from 'src/api/lists'
import { reportPoll } from 'src/api/polls'
import Button from '@mui/material/Button'

export type EndGameViewProps = {
  gameState: string
  mode: 'single' | 'multi' | 'host' | undefined
  multiMode: string
  teamOneScore: number
  teamTwoScore: number
  teamWinner?: number
  selectedList: number | undefined
  activePollId?: number
  hostPlayMode: string
  listItems: Item[]
  handleMiss: (itemId: number) => void
  handleNewGame: (options?: {
    silentPollStart?: boolean
    forceRandomOnlineLobby?: boolean
  }) => void
  handleHostPlayMode: (mode: string) => void
  soloResult: 'win' | 'loss' | 'active'
  soloGuessCount: number
  dailyList?: List
  dailyResult?: DailyListResult | undefined
  soloStats?: UserSoloStats
  soloXpAwarded?: number
  loading: boolean
  listRated?: number
  appUserId?: number
  user?: UserState
  doCreate: () => Promise<number | undefined>
  topic: string
  sounds: GameSounds
  handleEndGame: () => void
  handleRestartGame: () => void
  handleCreateList: () => void
  handleRateList: (rating: number) => void
  setRateAlert: (value: AlertInput) => void
  historyPush: (path: string) => void
  canControlOnlineActions?: boolean
  onLeaveOnlineLobby?: () => void
  onLeaveMatchmaking?: () => void
  handlePlayAgainMatchmaking?: () => void
  onlineRematchState?: OnlineRematchState
  onlineMatchRated?: boolean
  onlineMatchTrophyDelta?: number
  multiplayerXpAwarded?: number
  players?: Player[]
  connectionId?: string
  fullScreen?: boolean
}

const EndGameView = (props: EndGameViewProps) => {
  const {
    gameState,
    multiMode,
    mode,
    fullScreen,
    teamWinner,
    selectedList,
    activePollId,
    soloResult,
    soloGuessCount,
    dailyList,
    dailyResult,
    soloStats,
    soloXpAwarded,
    listItems,
    loading,
    appUserId,
    handleEndGame,
    handleRestartGame,
    handleNewGame,
    listRated,
    handleRateList,
    setRateAlert,
    user,
    doCreate,
    topic,
    sounds,
    canControlOnlineActions = true,
    onLeaveOnlineLobby,
    onLeaveMatchmaking,
    handlePlayAgainMatchmaking,
    onlineRematchState,
    onlineMatchRated,
    onlineMatchTrophyDelta,
    multiplayerXpAwarded,
    players = [],
    connectionId,
  } = props

  const [pollRated, setPollRated] = useState<number | undefined>(undefined)
  const [reportOpen, setReportOpen] = useState(false)
  const [reportDetails, setReportDetails] = useState('')
  const [reportSubmitting, setReportSubmitting] = useState(false)
  const reportMessage = reportDetails.trim()

  const [nowMs, setNowMs] = useState<number>(Date.now())
  const rematchRemainingMs = onlineRematchState
    ? Math.max(0, onlineRematchState.deadlineAtMs - nowMs)
    : 0
  const rematchProgress = onlineRematchState
    ? Math.min(100, Math.max(0, (rematchRemainingMs / 10000) * 100))
    : 0
  const myRematchVote = !!(
    onlineRematchState &&
    connectionId &&
    onlineRematchState.voters.includes(connectionId)
  )
  const rematchVoterPlayers =
    onlineRematchState?.voters
      .map((id) => players.find((player) => player.id === id))
      .filter((player): player is Player => !!player) || []
  const canRateList = !!user?.id
  const canRatePoll = !!user?.id
  const myPlayer = connectionId
    ? players.find((player) => player.id === connectionId)
    : undefined
  const didWinOnline = !!(
    myPlayer &&
    teamWinner &&
    teamWinner !== 0 &&
    myPlayer.team === teamWinner
  )
  const showOnlineRankedRewards =
    mode !== 'single' &&
    multiMode === 'online' &&
    !!onlineMatchRated &&
    (typeof onlineMatchTrophyDelta === 'number' ||
      typeof multiplayerXpAwarded === 'number')

  useEffect(() => {
    if (!onlineRematchState) return
    const timer = window.setInterval(() => {
      setNowMs(Date.now())
    }, 200)
    return () => window.clearInterval(timer)
  }, [onlineRematchState])

  useEffect(() => {
    if (!activePollId) {
      setPollRated(undefined)
      return
    }
    const stored = localStorage.getItem(`poll_rating_${activePollId}`)
    if (stored === '0' || stored === '1') {
      setPollRated(Number(stored))
    } else {
      setPollRated(undefined)
    }
  }, [activePollId])

  const handleRatePoll = (rating: number) => {
    sounds.click()
    if (!activePollId) return
    if (pollRated === rating) return
    setPollRated(rating)
    ratePoll({
      pollId: activePollId,
      rating,
      localKey: `poll_rating_${activePollId}`,
    })
      .then(() => {
        localStorage.setItem(`poll_rating_${activePollId}`, String(rating))
      })
      .catch((e) => err(e))
  }

  const handleSubmitReport = async () => {
    if (reportSubmitting) return
    if (!appUserId) return
    setReportSubmitting(true)
    try {
      const response = activePollId
        ? await reportPoll({
            pollId: activePollId,
            reason: 'user_report',
            details: reportDetails.trim() || undefined,
          })
        : selectedList
          ? await reportList({
              listId: selectedList,
              reason: 'user_report',
              details: reportDetails.trim() || undefined,
            })
          : undefined

      if (response?.success) {
        setRateAlert({
          open: true,
          severity: 'info',
          message: 'Report submitted. Thanks for helping improve RankTrivia.',
        })
        setReportOpen(false)
        setReportDetails('')
      } else {
        setRateAlert({
          open: true,
          severity: 'error',
          message: response?.message || 'Failed to submit report.',
        })
      }
    } catch (e) {
      err(e as Error)
      setRateAlert({
        open: true,
        severity: 'error',
        message: 'Failed to submit report.',
      })
    } finally {
      setReportSubmitting(false)
    }
  }

  const isPoll = !!activePollId
  const palette = isPoll
    ? {
        ink: '#693413',
        tint: altColorLighter,
        border: '#f1d5be',
        shadow: '#e8bb99',
        hover: '#fff6ed',
      }
    : {
        ink: mainColorDarkText,
        tint: mainColorLighter,
        border: borderColor,
        shadow: '#bfd4e3',
        hover: '#f1f8fc',
      }
  const actionSx = {
    minHeight: 48,
    borderRadius: '18px',
    px: 2,
    py: 1,
    fontFamily: headingFont,
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.2,
    color: palette.ink,
    background: '#ffffff',
    border: `1px solid ${palette.border}`,
    boxShadow: `0 3px 0 ${palette.shadow}`,
    '& .MuiTypography-root': {
      fontFamily: headingFont,
      fontSize: 'inherit',
      fontWeight: 800,
    },
    '& .MuiSvgIcon-root': { color: 'inherit', fontSize: 20 },
    '&:hover': {
      background: '#ffffff',
      borderColor: palette.border,
      boxShadow: `0 3px 0 ${palette.shadow}`,
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': { background: palette.hover, transform: 'translateY(-1px)' },
    },
    '&:active': {
      transform: 'translateY(2px)',
      boxShadow: `0 1px 0 ${palette.shadow}`,
    },
    '&.Mui-disabled': {
      background: palette.tint,
      color: isPoll ? '#987051' : '#6a8395',
      borderColor: palette.border,
      boxShadow: `0 2px 0 ${palette.shadow}`,
    },
  }
  const primaryActionSx = {
    ...actionSx,
    background: isPoll ? '#ffac78' : navy,
    borderColor: isPoll ? '#f5a36b' : '#345975',
    color: isPoll ? palette.ink : '#ffffff',
    boxShadow: isPoll
      ? '0 4px 0 #d78750, inset 0 1px 0 #ffdfc2'
      : '0 4px 0 #8db6cc, inset 0 1px 0 #466783',
    '&:hover': {
      background: isPoll ? '#ffac78' : navy,
      borderColor: isPoll ? '#f5a36b' : '#345975',
      boxShadow: isPoll
        ? '0 4px 0 #d78750, inset 0 1px 0 #ffdfc2'
        : '0 4px 0 #8db6cc, inset 0 1px 0 #466783',
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: isPoll ? '#ffac78' : navy,
        transform: 'translateY(-1px)',
      },
    },
    '&:active': {
      transform: 'translateY(2px)',
      boxShadow: `0 2px 0 ${isPoll ? '#d78750' : '#8db6cc'}`,
    },
  }
  const daily =
    mode === 'single' && dailyList?.id === selectedList
      ? dailyResult
      : undefined
  const xp =
    mode === 'single'
      ? soloStats
        ? soloXpAwarded
        : undefined
      : multiplayerXpAwarded
  const rating = isPoll ? pollRated : listRated
  const canRate = isPoll ? canRatePoll : canRateList
  const canShowRating = isPoll || !!selectedList
  const waitingForHost =
    multiMode === 'online' &&
    !canControlOnlineActions &&
    !handlePlayAgainMatchmaking &&
    !onLeaveMatchmaking
  const rewardSx = {
    height: 38,
    borderRadius: '12px',
    background: palette.tint,
    color: palette.ink,
    border: `1px solid ${palette.border}`,
    boxShadow: `0 2px 0 ${palette.shadow}`,
    fontFamily: headingFont,
    fontSize: 17,
    fontWeight: 900,
    '& .MuiChip-icon': { color: 'inherit', fontSize: 20 },
  }

  return gameState === 'over' ? (
    <Grow in={gameState === 'over'} timeout={350}>
      <Stack my={1}>
        <GlassCard
          sx={{
            p: { xs: 2, sm: 3 },
            borderColor: palette.border,
            boxShadow: isPoll
              ? '0 4px 0 #e8bb99, 0 12px 25px rgba(105,52,19,0.06)'
              : shadow2,
          }}
        >
          <Box
            data-testid="postgame-summary"
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: '22px',
              background: isPoll ? palette.tint : navy,
              color: isPoll ? palette.ink : '#ffffff',
              border: `1px solid ${isPoll ? palette.border : '#345975'}`,
              boxShadow: `0 3px 0 ${palette.shadow}`,
              '& .MuiTypography-root': { color: 'inherit' },
            }}
          >
            <Box
              component="img"
              src="/brand/podium.svg"
              alt=""
              sx={{
                width: 84,
                height: 80,
                display: 'block',
                mx: 'auto',
                mb: 1.5,
              }}
            />
            {mode !== 'single' &&
            teamWinner !== undefined &&
            teamWinner !== 0 ? (
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 600 }}
              >
                {multiMode === 'online'
                  ? didWinOnline
                    ? 'Match won!'
                    : 'Match lost!'
                  : `${teamWinner === 1 ? 'Orange' : 'Blue'} wins!`}
              </Typography>
            ) : null}
            {mode !== 'single' && teamWinner === 0 ? (
              <Typography variant="h4">Tie!</Typography>
            ) : null}
            {mode === 'single' ? (
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 25, sm: 30 },
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {soloResult === 'win'
                  ? daily
                    ? `Success! Ranked in ${daily.guesses} Guesses`
                    : `Completed in ${soloGuessCount} guesses!`
                  : daily
                    ? `Failed! ${daily.correct}/${daily.total} Correct`
                    : 'Better luck next time!'}
              </Typography>
            ) : null}
            {daily?.rank && daily.totalParticipants ? (
              <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
                gap={1}
                mt={2}
              >
                <Chip
                  data-testid="postgame-standing"
                  label={`Rank #${daily.rank} / ${daily.totalParticipants}`}
                  sx={{
                    ...rewardSx,
                    background: '#ffffff14',
                    color: isPoll ? palette.ink : '#e1f5fb',
                    borderColor: isPoll ? palette.border : '#5b7b94',
                    boxShadow: 'none',
                    fontSize: 15,
                  }}
                />
                {daily.topPercentLabel ? (
                  <Chip
                    label={daily.topPercentLabel}
                    sx={{ ...rewardSx, fontSize: 15 }}
                  />
                ) : null}
              </Stack>
            ) : null}
            {typeof xp === 'number' || showOnlineRankedRewards ? (
              <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
                gap={1}
                mt={2}
                sx={{ maxWidth: 300, mx: 'auto' }}
              >
                {showOnlineRankedRewards &&
                typeof onlineMatchTrophyDelta === 'number' ? (
                  <Chip
                    icon={<EmojiEventsRoundedIcon />}
                    label={`${onlineMatchTrophyDelta >= 0 ? '+' : ''}${onlineMatchTrophyDelta} Trophies`}
                    sx={{
                      ...rewardSx,
                      flex: '1 1 auto',
                      background:
                        onlineMatchTrophyDelta >= 0
                          ? mainColorLighter
                          : altColorLighter,
                      color:
                        onlineMatchTrophyDelta >= 0
                          ? mainColorDarkText
                          : altColorDarkText,
                    }}
                  />
                ) : null}
                {typeof xp === 'number' ? (
                  <Chip
                    data-testid="postgame-xp"
                    label={`+${xp} XP`}
                    sx={{
                      ...rewardSx,
                      flex: showOnlineRankedRewards ? '1 1 auto' : '1 1 100%',
                    }}
                  />
                ) : null}
              </Stack>
            ) : null}
          </Box>

          <Stack spacing={1} mt={3} alignItems="center">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              {canShowRating ? (
                <Tooltip title={!user?.id ? 'Log in to rate' : ''} arrow>
                  <Box
                    data-testid="postgame-rating"
                    role="group"
                    aria-label={`Rate this ${isPoll ? 'poll' : 'list'}`}
                    sx={{
                      display: 'flex',
                      minHeight: 48,
                      borderRadius: '18px',
                      border: `1px solid ${palette.border}`,
                      boxShadow: `0 3px 0 ${palette.shadow}`,
                      background: '#ffffff',
                    }}
                  >
                    {[0, 1].map((value) => (
                      <Button
                        key={value}
                        disabled={!canRate}
                        aria-pressed={rating === value}
                        onClick={() => {
                          if (isPoll) handleRatePoll(value)
                          else {
                            handleRateList(value)
                            setRateAlert({
                              open: true,
                              severity: 'success',
                              message: 'Thank you for the feedback!',
                            })
                          }
                        }}
                        startIcon={
                          value === 0 ? (
                            rating === 0 ? (
                              <ThumbDownIcon />
                            ) : (
                              <ThumbDownOutlinedIcon />
                            )
                          ) : rating === 1 ? (
                            <ThumbUpIcon />
                          ) : (
                            <ThumbUpOutlinedIcon />
                          )
                        }
                        sx={{
                          ...actionSx,
                          minHeight: 46,
                          minWidth: 0,
                          px: { xs: 1.5, sm: 2 },
                          fontSize: 14,
                          border: 'none',
                          borderRadius:
                            value === 0 ? '17px 0 0 17px' : '0 17px 17px 0',
                          boxShadow: 'none',
                          background:
                            rating === value ? palette.tint : '#ffffff',
                          '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                          '&:hover': {
                            background:
                              rating === value ? palette.tint : palette.hover,
                            boxShadow: 'none',
                          },
                          '@media (hover: hover) and (pointer: fine)': {
                            '&:hover': {
                              background:
                                rating === value ? palette.tint : palette.hover,
                              transform: 'none',
                            },
                          },
                          '&:active': {
                            transform: 'none',
                            background: palette.tint,
                            boxShadow: 'none',
                          },
                          '&.Mui-disabled': {
                            color: isPoll ? '#987051' : '#6a8395',
                            background: palette.tint,
                            boxShadow: 'none',
                          },
                          ...(value === 1
                            ? { borderLeft: `1px solid ${palette.border}` }
                            : {}),
                        }}
                      >
                        {value === 0 ? 'Not Fun' : 'Fun'}
                      </Button>
                    ))}
                  </Box>
                </Tooltip>
              ) : null}
              <ShareButton
                listId={(!activePollId ? selectedList : undefined) || undefined}
                pollId={activePollId}
                shareType={isPoll ? 'poll' : 'list'}
                pollShareMode={isPoll ? 'played' : undefined}
                score={soloGuessCount}
                listTitle={topic}
                result={soloResult}
                onCreate={doCreate}
                disabled={!activePollId && !selectedList && !appUserId}
                onClick={() => sounds.click()}
                variant="outlined"
                sx={{ ...actionSx, fontSize: 14 }}
              />
            </Box>
            {!user?.id && canShowRating ? (
              <Typography
                variant="caption"
                sx={{ color: isPoll ? '#987051' : helperTextColor }}
              >
                Log in to rate
              </Typography>
            ) : null}
          </Stack>

          {waitingForHost ? (
            <Typography mt={3} fontWeight={700} color="text.secondary">
              Waiting for host...
            </Typography>
          ) : (
            <Stack spacing={1.5} mt={3} alignItems="center">
              <GlassOutlineButton
                onClick={() => {
                  sounds.click()
                  if (multiMode === 'online') {
                    if (onLeaveMatchmaking) {
                      onLeaveMatchmaking()
                      return
                    }
                    if (!canControlOnlineActions && onLeaveOnlineLobby) {
                      onLeaveOnlineLobby()
                      return
                    }
                    handleEndGame()
                    return
                  }
                  handleEndGame()
                  handleRestartGame()
                }}
                fullWidth
                disabled={!listItems.length || loading}
                endIcon={<UndoIcon />}
                sx={{ ...actionSx, maxWidth: 300, minHeight: 52, fontSize: 18 }}
              >
                {multiMode === 'online'
                  ? onLeaveMatchmaking
                    ? 'Return'
                    : canControlOnlineActions
                      ? 'Lobby'
                      : 'Leave'
                  : 'Return'}
              </GlassOutlineButton>
              {!(onLeaveMatchmaking && !handlePlayAgainMatchmaking) ? (
                <GlassButton
                  onClick={() => {
                    sounds.click()
                    if (multiMode === 'online') {
                      if (handlePlayAgainMatchmaking) {
                        handlePlayAgainMatchmaking()
                        return
                      }
                      handleEndGame()
                      window.setTimeout(
                        () => handleNewGame({ forceRandomOnlineLobby: true }),
                        120,
                      )
                      return
                    }
                    handleEndGame()
                    handleNewGame()
                  }}
                  fullWidth
                  disabled={
                    !listItems.length ||
                    loading ||
                    !!(handlePlayAgainMatchmaking && myRematchVote)
                  }
                  endIcon={<PlayArrow />}
                  sx={{
                    ...primaryActionSx,
                    maxWidth: 300,
                    minHeight: 52,
                    fontSize: 18,
                  }}
                >
                  {multiMode === 'online'
                    ? handlePlayAgainMatchmaking
                      ? 'Play Another'
                      : 'New List'
                    : activePollId
                      ? 'New Poll'
                      : 'New Game'}
                </GlassButton>
              ) : null}
              {handlePlayAgainMatchmaking && onlineRematchState ? (
                <Box width="100%" maxWidth={300}>
                  <LinearProgress
                    variant="determinate"
                    value={rematchProgress}
                    sx={{
                      height: 6,
                      borderRadius: '999px',
                      background: palette.tint,
                      '& .MuiLinearProgress-bar': { background: palette.ink },
                    }}
                  />
                  <Box
                    mt={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {myRematchVote
                        ? 'Waiting for opponent...'
                        : 'Tap Play Again to accept'}
                    </Typography>
                    <AvatarGroup
                      max={2}
                      sx={{
                        '& .MuiAvatar-root': {
                          width: 24,
                          height: 24,
                          fontSize: 14,
                        },
                      }}
                    >
                      {rematchVoterPlayers.map((player) => (
                        <Avatar
                          key={`rematch-voter-${player.id || player.name}`}
                        >
                          {player.icon || '🙂'}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  </Box>
                </Box>
              ) : null}
            </Stack>
          )}
          {appUserId && (selectedList || activePollId) ? (
            <Box display="flex" justifyContent="flex-start" mt={3}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  sounds.click()
                  setReportOpen(true)
                }}
                sx={{
                  fontSize: 12,
                  color: helperTextColor,
                  minWidth: 0,
                  px: 0.75,
                  opacity: 0.7,
                }}
              >
                Report
              </Button>
            </Box>
          ) : null}
        </GlassCard>
        <Dialog
          open={reportOpen}
          onClose={() => {
            if (reportSubmitting) return
            setReportOpen(false)
            setReportDetails('')
          }}
          fullScreen={fullScreen}
          maxWidth="sm"
          fullWidth
          aria-labelledby="postgame-report-title"
          PaperProps={{
            sx: {
              borderRadius: fullScreen ? 0 : '28px',
              border: `1px solid ${palette.border}`,
              background: '#ffffff',
            },
          }}
        >
          <DialogTitle id="postgame-report-title">Report Content</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Please include details: inaccurate ranking, offensive content,
              spam, etc.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder="Add report details (required)"
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              disabled={reportSubmitting}
              helperText="Details are required."
              sx={{ '& .MuiOutlinedInput-root': { background: palette.tint } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <GlassOutlineButton
              onClick={() => {
                if (reportSubmitting) return
                setReportOpen(false)
                setReportDetails('')
              }}
              disabled={reportSubmitting}
              sx={actionSx}
            >
              Cancel
            </GlassOutlineButton>
            <GlassButton
              onClick={handleSubmitReport}
              disabled={reportSubmitting || !reportMessage}
              sx={primaryActionSx}
            >
              {reportSubmitting ? 'Submitting...' : 'Submit'}
            </GlassButton>
          </DialogActions>
        </Dialog>
      </Stack>
    </Grow>
  ) : null
}

export default EndGameView
